import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { X, ChevronRight, ChevronLeft, Sparkles, Image as ImageIcon, ListChecks, Target, Calendar, BookOpen, Trophy, DollarSign, Brain, Crown } from 'lucide-react';
import api from '../lib/api';

// Steps are linked to routes so the tour also navigates as you advance.
// Titles/bodies are referenced as i18n keys (tour_1_t .. tour_12_b).
const STEPS = [
  { titleKey: 'tour_1_t',  bodyKey: 'tour_1_b',  icon: Sparkles,   route: '/dashboard' },
  { titleKey: 'tour_2_t',  bodyKey: 'tour_2_b',  icon: Sparkles,   route: '/dashboard' },
  { titleKey: 'tour_3_t',  bodyKey: 'tour_3_b',  icon: ImageIcon,  route: '/vision-board' },
  { titleKey: 'tour_4_t',  bodyKey: 'tour_4_b',  icon: ListChecks, route: '/habits' },
  { titleKey: 'tour_5_t',  bodyKey: 'tour_5_b',  icon: Target,     route: '/goals' },
  { titleKey: 'tour_6_t',  bodyKey: 'tour_6_b',  icon: Calendar,   route: '/calendar' },
  { titleKey: 'tour_7_t',  bodyKey: 'tour_7_b',  icon: BookOpen,   route: '/journal' },
  { titleKey: 'tour_8_t',  bodyKey: 'tour_8_b',  icon: Trophy,     route: '/achievements' },
  { titleKey: 'tour_9_t',  bodyKey: 'tour_9_b',  icon: Brain,      route: '/clarity' },
  { titleKey: 'tour_10_t', bodyKey: 'tour_10_b', icon: DollarSign, route: '/budget' },
  { titleKey: 'tour_11_t', bodyKey: 'tour_11_b', icon: Crown,      route: '/membership' },
  { titleKey: 'tour_12_t', bodyKey: 'tour_12_b', icon: Sparkles,   route: '/dashboard' },
];

const OnboardingTour = () => {
  const { user, refreshUser } = useAuth();
  const { theme } = useTheme();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [open, setOpen] = useState(false);
  const [forced, setForced] = useState(false); // when user explicitly opens from the Help button

  // Auto-open on first login when tutorial is not completed/skipped.
  useEffect(() => {
    if (!user) return;
    const tut = user.tutorial || {};
    if (!tut.completed && !tut.skipped) {
      setStep(tut.step || 0);
      setOpen(true);
    }
  }, [user]);

  // Listen to external open events (from header help button).
  useEffect(() => {
    const onOpen = () => { setStep(0); setForced(true); setOpen(true); };
    window.addEventListener('teko-tour-open', onOpen);
    return () => window.removeEventListener('teko-tour-open', onOpen);
  }, []);

  const saveState = async (patch) => {
    try {
      await api.put('/tutorial', patch);
      await refreshUser?.();
    } catch (err) { console.debug('tutorial save failed', err); }
  };

  const next = () => {
    const newStep = step + 1;
    if (newStep >= STEPS.length) {
      finish();
    } else {
      setStep(newStep);
      const s = STEPS[newStep];
      if (s.route) navigate(s.route);
      saveState({ step: newStep });
    }
  };

  const prev = () => {
    const newStep = Math.max(0, step - 1);
    setStep(newStep);
    const s = STEPS[newStep];
    if (s.route) navigate(s.route);
  };

  const skip = () => {
    setOpen(false);
    if (!forced) saveState({ skipped: true });
  };

  const finish = () => {
    setOpen(false);
    saveState({ completed: true, step: STEPS.length });
  };

  if (!open) return null;

  const current = STEPS[step];
  const Icon = current.icon || Sparkles;
  const pct = ((step + 1) / STEPS.length) * 100;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
      data-testid="tutorial-tour"
    >
      <div
        className="relative w-full max-w-md rounded-3xl overflow-hidden"
        style={{ background: theme.card, border: `1px solid ${theme.accent}50`, boxShadow: `0 24px 80px ${theme.accent}40` }}
      >
        {/* Progress bar */}
        <div className="h-1 w-full" style={{ background: theme.elevated }}>
          <div className="h-full transition-all" style={{ width: `${pct}%`, background: theme.accent }} />
        </div>

        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${theme.accent}25`, border: `1px solid ${theme.accent}60` }}>
                <Icon size={20} style={{ color: theme.accent }} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest" style={{ color: theme.textMuted }}>{t('tour_step')} {step + 1} {t('tour_of')} {STEPS.length}</p>
                <h3 className="text-xl font-medium" style={{ color: theme.textPrimary }}>{t(current.titleKey)}</h3>
              </div>
            </div>
            <button onClick={skip} aria-label={t('tour_close')} data-testid="tutorial-skip-x"
              className="p-1 hover:opacity-70 transition" style={{ color: theme.textMuted }}>
              <X size={18} />
            </button>
          </div>

          <p className="text-sm leading-relaxed mb-6" style={{ color: theme.textSecondary }}>
            {t(current.bodyKey)}
          </p>

          <div className="flex items-center justify-between gap-2">
            <button onClick={skip} className="text-xs underline" style={{ color: theme.textMuted }} data-testid="tutorial-skip">
              {t('tour_skip')}
            </button>
            <div className="flex gap-2">
              {step > 0 && (
                <Button onClick={prev} variant="outline" size="sm"
                  style={{ borderColor: theme.border, color: theme.textSecondary }}
                  data-testid="tutorial-prev">
                  <ChevronLeft size={14} className="mr-1" />{t('tour_prev')}
                </Button>
              )}
              <Button onClick={next} size="sm"
                style={{ background: theme.accent, color: theme.bg }}
                data-testid="tutorial-next">
                {step === STEPS.length - 1 ? t('tour_start') : t('tour_next')}
                {step < STEPS.length - 1 && <ChevronRight size={14} className="ml-1" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
